/**
 * Forward Fibonacci time projections from the two most recent pivots.
 *
 * Unlike the Fibonacci Pivot Pairs study, which searches the whole chart for pairs whose ratios
 * are already confirmed by other pivots, this asks a single forward question: taking the interval
 * between the last two swings as the unit, where do the Fibonacci multiples of it fall next?
 *
 *   P2        P1
 *   |----D----|-----0.618D----|------1.0D------|-------1.618D-------|
 *             ^ anchor        ^ target         ^ target             ^ target
 *
 * Every target is measured forward from P1, the more recent pivot, because the question is where
 * the NEXT turn is due — measuring from P2 would answer where P1 was due, which is already known.
 *
 * Targets landing in history can be checked against what price actually did; targets landing past
 * the last bar cannot, and are labelled as targets rather than confirmations.
 */

import { detectPivots, DEFAULT_PIVOT_STRENGTH, PIVOT_HIGH } from './pivots';
import { accentFor, PROJECTION_WIDTH } from './overlayInk';

/** Multipliers applied to the interval between the two most recent pivots. */
export const PROJECTION_RATIOS = [0.382, 0.618, 1.0, 1.272, 1.618, 2.0, 2.618, 4.236];

/** Bars either side of a target within which an existing pivot counts as having hit it. */
export const HIT_TOLERANCE = 1;

/** Ratio formatted for a label: 1.618, 2, 0.382 — trailing zeros trimmed. */
export function formatRatio(ratio) {
  if (typeof ratio !== 'number' || !Number.isFinite(ratio)) return '';
  return String(parseFloat(ratio.toFixed(3)));
}

/**
 * The two most recent pivots, most recent first.
 *
 * Any combination of highs and lows is accepted: a time interval between swings does not care
 * which kind of extreme sits at each end, only how many bars apart they are.
 *
 * @returns {{p1:object, p2:object, distance:number}|null} null when there are not two to work with
 */
export function recentPivotPair(pivots) {
  if (!Array.isArray(pivots) || pivots.length < 2) return null;
  const ordered = [...pivots]
    .filter(p => p && typeof p.index === 'number')
    .sort((a, b) => a.index - b.index);
  if (ordered.length < 2) return null;

  const p1 = ordered[ordered.length - 1];
  const p2 = ordered[ordered.length - 2];
  const distance = Math.abs(p1.index - p2.index);
  // Two pivots on the same bar give no interval to project.
  if (distance === 0) return null;
  return { p1, p2, distance };
}

/**
 * Projected target bar indices, with each one classified.
 *
 * The index is floored, per the brief: a fractional bar does not exist, and rounding would move
 * some targets a bar later than the arithmetic gives.
 *
 * @param {object}   pair          from recentPivotPair
 * @param {number}   lastRealIndex last bar carrying price
 * @param {number}   maxIndex      last addressable bar, including padded future space
 * @param {Array}    pivots        all pivots, for checking whether a historical target was hit
 * @returns {Array<{ratio:number, index:number, isFuture:boolean, hit:object|null, label:string}>}
 */
export function projectTargets(pair, lastRealIndex, maxIndex, pivots = [], ratios = PROJECTION_RATIOS) {
  if (!pair || !Number.isFinite(lastRealIndex) || !Number.isFinite(maxIndex)) return [];

  const byIndex = new Map();
  (pivots || []).forEach(p => {
    if (p && typeof p.index === 'number' && !byIndex.has(p.index)) byIndex.set(p.index, p);
  });

  const out = [];
  ratios.forEach(ratio => {
    const index = pair.p1.index + Math.floor(pair.distance * ratio);
    // Off the end of the addressable chart: there is no bar to draw on, and emitting it anyway
    // would have the renderer drop it silently while the summary still counted it.
    if (index > maxIndex || index < 0) return;

    const isFuture = index > lastRealIndex;

    // A historical target counts as hit when a real pivot sits within tolerance of it.
    let hit = null;
    if (!isFuture) {
      for (let d = -HIT_TOLERANCE; d <= HIT_TOLERANCE; d++) {
        const cand = byIndex.get(index + d);
        // The anchor is not evidence for its own projection.
        if (cand && cand.index !== pair.p1.index) { hit = cand; break; }
      }
    }

    out.push({
      ratio,
      index,
      isFuture,
      hit,
      barsAhead: isFuture ? index - lastRealIndex : 0,
      label: isFuture ? `Fib ${formatRatio(ratio)} Target` : `Fib ${formatRatio(ratio)}`,
    });
  });

  return out.sort((a, b) => a.index - b.index);
}

/**
 * Builds the overlays for the tool: vertical lines, dots on hits, and a planning guide.
 *
 * Admin-only, checked here rather than only on the button, since a hidden control is a convenience
 * and not a guard.
 *
 * @returns {{overlays:Array, summary:object}|null}
 */
export function buildRecentPivotProjection(barData, {
  isAdmin = false, strength = DEFAULT_PIVOT_STRENGTH, extendedDates = null, ratios = PROJECTION_RATIOS,
} = {}) {
  if (!isAdmin) return null;
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return null;

  const { dates, ohlc } = barData;
  if (dates.length === 0) return null;

  const { all: pivots } = detectPivots(ohlc, strength);
  const pair = recentPivotPair(pivots);
  if (!pair) return null;

  // Future targets need the padded dates: the price series stops at the last bar, so a target one
  // bar later has no date to sit on and could not be drawn at all.
  const projectionDates = Array.isArray(extendedDates) && extendedDates.length > dates.length
    ? extendedDates
    : dates;
  const lastRealIndex = dates.length - 1;

  const targets = projectTargets(pair, lastRealIndex, projectionDates.length - 1, pivots, ratios);
  if (targets.length === 0) return null;

  const overlays = [];

  // The two anchors, so the interval being projected is visible rather than implied.
  [pair.p2, pair.p1].forEach((p, i) => {
    overlays.push({
      type: 'vLine',
      date: dates[p.index],
      // ⚠ WAS `#455a64`, which measures 2.61:1 against the dark canvas — below the 3:1 floor at
      // which a graphical object registers. Theme-aware now; see overlayInk.js for why a single
      // bright value was refused.
      color: accentFor('anchor'),
      lineType: 'solid',
      // Set per-overlay rather than by raising the renderer's default, which would flatten the
      // deliberate 2-against-1 hierarchies in `fixedCycles` and `spiralInspectOverlays`.
      thickness: PROJECTION_WIDTH,
      label: i === 0 ? 'P2' : 'P1',
      id: `recentfib-anchor-${p.index}`,
    });
  });

  targets.forEach(t => {
    overlays.push({
      type: 'vLine',
      date: projectionDates[t.index],
      // Future targets are dashed and warm-coloured; a historical one is solid and cool. The
      // distinction is the point of the tool — one is a prediction, the other is checkable.
      // Both theme-aware: the hues keep their meaning, the VALUE follows the canvas.
      color: t.isFuture ? accentFor('future') : accentFor('historical'),
      lineType: t.isFuture ? 'dashed' : 'dotted',
      thickness: PROJECTION_WIDTH,
      label: t.isFuture ? `${t.label}\n+${t.barsAhead} bars` : t.label,
      id: `recentfib-target-${t.ratio}-${t.index}`,
    });
  });

  // Dots on the historical targets price actually turned at.
  const hits = targets.filter(t => t.hit);
  if (hits.length > 0) {
    overlays.push({
      type: 'pivotMarkers',
      label: 'Recent-pivot Fib hits',
      symbol: 'circle',
      symbolSize: 9,
      color: '#0277bd',
      points: hits.map(t => ({
        date: dates[t.hit.index],
        price: t.hit.price,
        position: t.hit.type === PIVOT_HIGH ? 'above' : 'below',
        color: '#0277bd',
        label: formatRatio(t.ratio),
      })),
      id: `recentfib-hits-${hits.length}`,
    });
  }

  // A horizontal guide at the current close, run out to the furthest future target, so a target
  // date can be read against the level price is actually at.
  const future = targets.filter(t => t.isFuture);
  const lastClose = ohlc[lastRealIndex] ? ohlc[lastRealIndex][1] : null;
  if (future.length > 0 && typeof lastClose === 'number' && Number.isFinite(lastClose)) {
    const furthest = future[future.length - 1];
    overlays.push({
      type: 'trendline',
      tool: 'projectionGuide',
      date1: dates[lastRealIndex],
      price1: lastClose,
      date2: projectionDates[furthest.index],
      price2: lastClose,
      color: '#e65100',
      lineType: 'dashed',
      lineWidth: 1,
      // No `source`, so it earns no move badge: it is a level, not a measured trade.
      id: `recentfib-guide-${furthest.index}`,
    });
  }

  return {
    overlays,
    summary: {
      p1Index: pair.p1.index,
      p2Index: pair.p2.index,
      p1Date: dates[pair.p1.index],
      p2Date: dates[pair.p2.index],
      distance: pair.distance,
      targetCount: targets.length,
      futureCount: future.length,
      hitCount: hits.length,
      nextTargetBars: future.length ? future[0].barsAhead : null,
    },
  };
}

/** Marks every overlay this tool creates, so toggling can find and remove them. */
export const RECENT_FIB_PREFIX = 'recentfib-';

/** Whether the tool's overlays are currently on the chart. */
export function hasRecentFibOverlays(overlays) {
  return Array.isArray(overlays)
    && overlays.some(o => o && typeof o.id === 'string' && o.id.startsWith(RECENT_FIB_PREFIX));
}

/** The overlay list with this tool's overlays removed. */
export function withoutRecentFibOverlays(overlays) {
  if (!Array.isArray(overlays)) return [];
  return overlays.filter(o => !(o && typeof o.id === 'string' && o.id.startsWith(RECENT_FIB_PREFIX)));
}
